import type { FieldSchemaModel } from '@/shared/generated'
import type { ChangeEvent, KeyboardEvent } from 'react'

import React, { useState } from 'react'
import { isNaN } from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { getEntityFieldValue } from '@/entities/assortment/@x/column'

import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Input } from '@/shared/components/ui/input'
import { LinkButton } from '@/shared/components/ui/link-button'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import { ampli } from '@/shared/lib/ampli'
import { cn } from '@/shared/lib/utils'
import { numericOnly } from '@/shared/tools/constants'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { BasicPriceField } from './BasicPriceField'

const convertValue = (val?: number | string | null) =>
	val ? (isNaN(Number(val)) ? val : Number(val).toFixed(2)) : ''
function Content({
	onChange,
	row,
	value,
	field,
	close,
	disabled,
	disabledReason,
}: {
	onChange: (value: number) => void
	row: any
	value: number | null
	field?: FieldSchemaModel
	close: () => void
	disabled?: boolean
	disabledReason?: string
}) {
	const { t } = useTranslation()

	const options = field?.options as any
	const currentKey: string = options?.data?.init_column_name ?? 'last_price'
	const dependedKey: string =
		options?.data?.optimal_column_name ?? 'optimal_price'
	const currValue: number | null = getEntityFieldValue(row, currentKey)
	const dependedValue: number | null = getEntityFieldValue(row, dependedKey)
	const initialValue = value ? convertValue(value) : value
	const [localValue, setLocalValue] = useState(initialValue)
	const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
		setLocalValue(event.target.value.replace(numericOnly, ''))
	const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) =>
		event.keyCode === 13 && handleConfirm()
	const handleBlur = () => (localValue != initialValue ? handleConfirm() : null)
	const handleChangePrice = (value: number) => {
		if (!value) return
		onChange?.(value)
		close()
	}
	const _handleSetOptimal = () => {
		if (dependedValue) {
			ampli.tableNewPriceEditing({ 'Set new price': 'Optimal' })
			handleChangePrice(dependedValue as number)
		}
	}
	const _handleSetCurrent = () => {
		if (currValue) {
			ampli.tableNewPriceEditing({ 'Set new price': 'Current' })
			handleChangePrice(currValue as number)
		}
	}
	const handleConfirm = () => {
		ampli.tableNewPriceEditing({ 'Set new price': 'Manual' })
		const valueToSet =
			isNumberValid(localValue as string) && Number(localValue) > 0
				? localValue
				: row.fields[currentKey]
		onChange?.(valueToSet)
		setLocalValue(convertValue(valueToSet))
		close()
	}

	return (
		<div className='flex w-72 flex-col'>
			<div className='mb-4 text-sm leading-none font-semibold'>
				{t('products_select_new_price')}
			</div>
			<dl className='grid grid-cols-1 text-base/6 sm:grid-cols-[min(50%,--spacing(80))_auto] sm:text-sm/6'>
				<dt className='col-start-1 flex items-center border-t pt-3 text-muted-foreground first:border-none sm:border-t sm:py-2'>
					{t('products_current')}
				</dt>
				<dd className='pt-1 pb-3 sm:border-t sm:py-2 sm:nth-2:border-none'>
					<LinkButton disabled={disabled} onClick={_handleSetCurrent}>
						{currValue !== null
							? formatNumber(currValue, NumberFormats.Fixed)
							: '-'}
					</LinkButton>
				</dd>
				<dt className='col-start-1 flex items-center border-t pt-3 text-muted-foreground first:border-none sm:border-t sm:py-2'>
					{t('products_optimized')}
				</dt>
				<dd className='pt-1 pb-3 sm:border-t sm:py-2 sm:nth-2:border-none'>
					{dependedValue !== null ? (
						<LinkButton
							disabled={disabled}
							onClick={_handleSetOptimal}
							className='font-medium'
						>
							{formatNumber(dependedValue, NumberFormats.Fixed)}
						</LinkButton>
					) : (
						'-'
					)}
				</dd>
				<dt className='col-start-1 flex items-center border-t pt-3 text-muted-foreground first:border-none sm:border-t sm:py-2'>
					{t('products_manual')}
				</dt>
				<dd className='pt-1 pb-3 sm:border-t sm:py-2 sm:nth-2:border-none'>
					<Input
						autoFocus
						type='number'
						className='w-full'
						disabled={disabled}
						value={localValue as string}
						onBlur={handleBlur}
						onKeyUp={handleKeyUp}
						onChange={handleChange}
					/>
				</dd>
			</dl>
			{!!disabledReason && (
				<Alert variant='warning'>
					<AlertTriangleIcon className='size-4 text-destructive' />
					<AlertDescription>{disabledReason}</AlertDescription>
				</Alert>
			)}
		</div>
	)
}
export function BasicPriceEditorField(props: {
	value: number | null
	isDirty: boolean
	field?: FieldSchemaModel
	onChange: (value: number) => void
	row: any
	disabled?: boolean
	disabledReason?: string
}) {
	const [open, setOpen] = useState(false)

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<div
					className={cn(
						'inline-flex size-full cursor-pointer items-center underline-offset-4 hover:underline hover:decoration-dashed',
						{
							'text-primary': props.isDirty,
						}
					)}
				>
					<BasicPriceField value={props.value} />
				</div>
			</PopoverTrigger>
			<PopoverContent className='w-auto'>
				<Content {...props} close={() => setOpen(false)} />
			</PopoverContent>
		</Popover>
	)
}
const isNumberValid = (value: string) => {
	const regexp = /^-?\d*(\.\d*|,\d*)?$/

	return regexp.test(value)
}
